import React, { useCallback } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress, Tooltip, Typography } from "@mui/material";
import notificationStore from "../../../../../stores/Notification/notificationStore";
import {
  DebitPermissionDto,
  PatientBillInfoDto,
  CreateRefundDto,
  CreateRefundDtoTypeEnum,
  CreateRefundDtoMethodEnum,
} from "../../../../../api-client";
import { usePatientExistingRxData } from "../../hooks/usePatientExistingRxData";
import { observer } from "mobx-react";
import { useCreateRefund } from "../../../../../stores/Transactions/transaction.mutations";

interface RefundTransactionFormProps {
  open: boolean;
  handleTransactionClose: () => void;
  patientId: number;
  rxId: string;
  userId: string;
  locationId: string;
  creditPermission?: DebitPermissionDto;
  handleReturnToPatient: (bill: PatientBillInfoDto) => Promise<void>;
}

const RefundTransactionForm: React.FC<RefundTransactionFormProps> = observer(
  ({
    open,
    handleTransactionClose,
    patientId,
    rxId,
    userId,
    locationId,
    creditPermission,
    handleReturnToPatient,
  }) => {
    const { data: billData, isLoading } = usePatientExistingRxData(
      patientId,
      rxId,
    );
    const { mutate: createRefund, isPending: isCreatingRefund } =
      useCreateRefund();

    const handleSubmit = useCallback(
      async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!billData) {
          notificationStore.showMessage("Daten nicht verfügbar", "error");
          return;
        }

        try {
          if (!creditPermission) {
            // Cash refund
            await handleReturnToPatient(billData);
            handleTransactionClose();
          } else {
            // SEPA credit transfer refund
            const tAccounts = billData.tarifGroupId !== "Privat" ? 4101 : 4105;

            const refundDto: CreateRefundDto = {
              amount: Math.abs(billData.dueAmount),
              text: "Rückerstattung via SEPA Überweisung",
              patientId: patientId,
              creditPermissionId: creditPermission.id,
              rxId: rxId,
              accountId: billData.accountId,
              userId: userId,
              tAccountId: tAccounts,
              businessId: billData.businessId,
              locationId: locationId,
              method: CreateRefundDtoMethodEnum.Lastschrift,
              type: CreateRefundDtoTypeEnum.Rckerstattung
            };

            createRefund(refundDto, {
              onSuccess: () => {
                notificationStore.showMessage(
                  "Rückerstattung erfolgreich erstellt",
                  "success",
                );
                handleTransactionClose();
              },
              onError: (error: any) => {
                console.error("Error creating refund:", error);
                notificationStore.showMessage(
                  "Fehler beim Erstellen der Rückerstattung",
                  "error",
                );
              },
            });
          }
        } catch (error) {
          console.error("Refund creation failed:", error);
        }
      },
      [
        billData,
        creditPermission,
        handleReturnToPatient,
        handleTransactionClose,
        patientId,
        rxId,
        userId,
        locationId,
        createRefund,
      ],
    );

    if (isLoading || !billData) {
      return (
        <Dialog open={open} onClose={handleTransactionClose}>
          <DialogContent
            sx={{ display: "flex", justifyContent: "center", p: 4 }}
          >
            <CircularProgress />
          </DialogContent>
        </Dialog>
      );
    }

    const isDisabled = !billData || billData.dueAmount >= 0;

    return (
      <Dialog
        open={open}
        onClose={handleTransactionClose}
        PaperProps={{ component: "form", onSubmit: handleSubmit }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "h6.fontSize",
            backgroundColor: "white",
            color: "secondary.main",
            textAlign: "center",
          }}
        >
          Rückerstattung
        </DialogTitle>
        <DialogContent sx={{ my: 1 }}>
          {creditPermission && (
            <Typography variant="h6" sx={{}}>
              <strong>Gutschrift: </strong>
              {creditPermission.firstName} {creditPermission.lastName}
            </Typography>
          )}

          <Typography variant="h6" sx={{}}>
            <strong>Motiv: </strong>
            Rückerstattung
          </Typography>

          <Typography variant="h6" sx={{}}>
            <strong>Mandant: </strong>
            {billData.accountId || "Das Rezept hat keinen Mandanten"}
          </Typography>

          <Typography variant="h6" sx={{ marginBottom: 1 }}>
            <strong>Rückerstattungsbetrag: </strong>
            {Math.abs(billData.dueAmount).toFixed(2).replace(".", ",")}€
            {billData.isProvisionalPrice
              ? " (Geschätzt - Noch sind nicht alle Termine festgelegt)"
              : ""}
          </Typography>

          <DialogContentText sx={{ marginBottom: 1 }}>
            Bitte bestätigen Sie die Rückerstattung an den Patienten.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTransactionClose}>Abbrechen</Button>
          <Tooltip
            title={isDisabled ? "Keine Rückerstattung erforderlich" : ""}
            arrow
          >
            <span>
              <Button
                type="submit"
                disabled={isDisabled || isCreatingRefund}
                style={{ pointerEvents: isDisabled ? "none" : "auto" }}
              >
                {isCreatingRefund ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  "Rückerstattung durchführen"
                )}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    );
  },
);

export default RefundTransactionForm;
